package dev.jfronny.zerointerest.ui.component

import androidx.compose.foundation.layout.Column
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import de.connect2x.trixnity.client.verification
import de.connect2x.trixnity.client.verification.ActiveSasVerificationMethod
import de.connect2x.trixnity.client.verification.ActiveSasVerificationState
import de.connect2x.trixnity.client.verification.ActiveVerificationState
import de.connect2x.trixnity.core.model.events.m.key.verification.VerificationMethod
import dev.jfronny.zerointerest.service.client.MatrixClientService
import dev.jfronny.zerointerest.shared.generated.resources.*
import io.github.oshai.kotlinlogging.KotlinLogging
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject

private val log = KotlinLogging.logger {}

@Composable
fun VerificationDialog() {
    val rxclient by koinInject<MatrixClientService>().client.collectAsState(null)
    val client = rxclient

    if (client != null) {
        val verification by remember(client) { client.verification.activeDeviceVerification }.collectAsState()
        var mutVer by remember(verification) { mutableStateOf(verification) }
        mutVer?.let {
            val state by it.state.collectAsState()
            val coroutineScope = rememberCoroutineScope()
            VerificationContent(
                onClose = { mutVer = null },
                state = state,
                onCancel = { coroutineScope.launch { it.cancel() } },
                onStart = { state, method -> coroutineScope.launch { state.start(method) } },
                onReady = { state -> coroutineScope.launch { state.ready() } },
                onMatch = { state -> coroutineScope.launch { state.match() } },
                onMismatch = { state -> coroutineScope.launch { state.noMatch() } },
            )
        }
    }
}

@Composable
private fun VerificationContent(
    onClose: () -> Unit,
    state: ActiveVerificationState,
    onCancel: () -> Unit,
    onStart: (state: ActiveVerificationState.Ready, method: VerificationMethod) -> Unit,
    onReady: (state: ActiveVerificationState.TheirRequest) -> Unit,
    onMatch: (state: ActiveSasVerificationState.ComparisonByUser) -> Unit,
    onMismatch: (state: ActiveSasVerificationState.ComparisonByUser) -> Unit,
) {
    when (state) {
        is ActiveVerificationState.OwnRequest -> {
            AlertDialog(
                onDismissRequest = onCancel,
                title = { Text(stringResource(Res.string.verification_request_sent)) },
                text = { Text(stringResource(Res.string.waiting_for_other_device_accept)) },
                confirmButton = {
                    Button(onClick = onCancel) {
                        Text(stringResource(Res.string.cancel))
                    }
                },
            )
        }

        is ActiveVerificationState.TheirRequest -> {
            AlertDialog(
                onDismissRequest = onCancel,
                title = { Text(stringResource(Res.string.verification_request)) },
                text = { Text(stringResource(Res.string.another_device_requesting_verification)) },
                confirmButton = {
                    Button(onClick = { onReady(state) }) {
                        Text(stringResource(Res.string.accept))
                    }
                },
                dismissButton = {
                    Button(onClick = onCancel) {
                        Text(stringResource(Res.string.decline))
                    }
                },
            )
        }

        is ActiveVerificationState.Ready -> {
            val sas = state.methods.firstNotNullOfOrNull { it as? VerificationMethod.Sas }
            if (sas != null) {
                onStart(state, sas)
            } else {
                AlertDialog(
                    onDismissRequest = onCancel,
                    title = { Text(stringResource(Res.string.choose_verification_method)) },
                    text = {
                        Column {
                            state.methods.forEach { method ->
                                Button(onClick = { onStart(state, method) }) {
                                    Text(method.toString())
                                }
                            }
                        }
                    },
                    confirmButton = {},
                )
            }
        }

        is ActiveVerificationState.Start -> {
            // For now, we only handle SAS verification
            val sas = state.method as? ActiveSasVerificationMethod
            if (sas != null) {
                val sasState by sas.state.collectAsState()
                when (val activeSasState = sasState) {
                    is ActiveSasVerificationState.ComparisonByUser -> {
                        AlertDialog(
                            onDismissRequest = onCancel,
                            title = { Text(stringResource(Res.string.compare)) },
                            text = {
                                Text(
                                    stringResource(
                                        Res.string.compare_emoji_sequence,
                                        activeSasState.emojis.joinToString(" ") { it.second },
                                    ),
                                )
                            },
                            confirmButton = {
                                Button(onClick = { onMatch(activeSasState) }) {
                                    Text(stringResource(Res.string.match))
                                }
                            },
                            dismissButton = {
                                Button(onClick = { onMismatch(activeSasState) }) {
                                    Text(stringResource(Res.string.mismatch))
                                }
                            },
                        )
                    }

                    else -> {
                        AlertDialog(
                            onDismissRequest = onCancel,
                            title = { Text(stringResource(Res.string.verification_in_progress)) },
                            text = { Text(stringResource(Res.string.follow_instructions_other_device)) },
                            confirmButton = {
                                Button(onClick = onCancel) {
                                    Text(stringResource(Res.string.cancel))
                                }
                            },
                        )
                    }
                }
            }
        }

        is ActiveVerificationState.WaitForDone -> {
            AlertDialog(
                onDismissRequest = { },
                title = { Text(stringResource(Res.string.waiting_for_other_device)) },
                text = { Text(stringResource(Res.string.waiting_for_other_device_finish)) },
                confirmButton = {},
            )
        }

        ActiveVerificationState.Done -> {
            AlertDialog(
                onDismissRequest = onClose,
                title = { Text(stringResource(Res.string.verification_successful)) },
                text = { Text(stringResource(Res.string.device_verified_successfully)) },
                confirmButton = {
                    Button(onClick = onClose) {
                        Text(stringResource(Res.string.ok))
                    }
                },
            )
        }

        is ActiveVerificationState.Cancel -> {
            AlertDialog(
                onDismissRequest = onClose,
                title = { Text(stringResource(Res.string.verification_cancelled)) },
                text = {
                    val who = if (state.isOurOwn) stringResource(Res.string.you) else stringResource(Res.string.the_other_device)
                    Text(stringResource(Res.string.verification_cancelled_by, who, state.content.reason))
                },
                confirmButton = {
                    Button(onClick = onClose) {
                        Text(stringResource(Res.string.ok))
                    }
                },
            )
        }

        ActiveVerificationState.AcceptedByOtherDevice -> {
            AlertDialog(
                onDismissRequest = onClose,
                title = { Text(stringResource(Res.string.verification_accepted)) },
                text = { Text(stringResource(Res.string.another_device_accepted_verification)) },
                confirmButton = {
                    Button(onClick = onClose) {
                        Text(stringResource(Res.string.ok))
                    }
                },
            )
        }

        ActiveVerificationState.Undefined -> {
            AlertDialog(
                onDismissRequest = onClose,
                title = { Text(stringResource(Res.string.error)) },
                text = { Text(stringResource(Res.string.unexpected_error_verification)) },
                confirmButton = {
                    Button(onClick = onClose) {
                        Text(stringResource(Res.string.ok))
                    }
                },
            )
        }
    }
}
